import { resolveAgentServiceTicketCount } from "./agentServiceTimeMetrics";

const toNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const uniqueCount = (values = []) => new Set(values.filter(Boolean)).size;

const getAgentRows = (branch = {}) =>
  (branch.servicesData || []).flatMap((service) =>
    (service.agentsData || []).map((agent) => ({
      ...agent,
      branchName: branch.branchName,
      serviceId: service.serviceId || agent.serviceId,
      serviceName: service.serviceName || agent.serviceName || "",
      completedTickets: resolveAgentServiceTicketCount(agent),
      totalServiceTime: toNumber(agent.sum),
      averageServiceTime: toNumber(agent.avgPerTicket ?? agent.average),
      flags: Array.isArray(agent.flags) ? agent.flags : [],
    }))
  );

const rankRows = (rows, key, field, direction = "desc") => {
  [...rows]
    .sort((left, right) => {
      const leftValue = toNumber(left[key]);
      const rightValue = toNumber(right[key]);
      return direction === "asc" ? leftValue - rightValue : rightValue - leftValue;
    })
    .forEach((row, index) => {
      row[field] = index + 1;
    });
  return rows;
};

export const getAgentReviewPriorityRows = (agentSummary = [], limit = Infinity) =>
  [...agentSummary]
    .filter((agent) => toNumber(agent.completedTickets) > 0)
    .sort(
      (left, right) =>
        (right.effectivenessBand === "Needs review" ? 1 : 0) -
          (left.effectivenessBand === "Needs review" ? 1 : 0) ||
        (right.flags?.length || 0) - (left.flags?.length || 0) ||
        toNumber(right.avgVsBranchAveragePct) - toNumber(left.avgVsBranchAveragePct) ||
        toNumber(right.workloadIndex) - toNumber(left.workloadIndex) ||
        toNumber(right.completedTickets) - toNumber(left.completedTickets)
    )
    .slice(0, limit);

const buildAgentSummary = (branch = {}) => {
  if (Array.isArray(branch.agentSummary) && branch.agentSummary.length) {
    return branch.agentSummary.map((agent) => ({
      ...agent,
      completedTickets: toNumber(agent.completedTickets ?? agent.ticketCount),
      totalServiceTime: toNumber(agent.totalServiceTime ?? agent.sum),
      averageServiceTime: toNumber(agent.averageServiceTime ?? agent.average),
      flags: Array.isArray(agent.flags) ? agent.flags : [],
    }));
  }

  const map = new Map();
  getAgentRows(branch).forEach((row) => {
    const key = row.agentId || row.agentName;
    if (!map.has(key)) {
      map.set(key, {
        agentId: row.agentId,
        agentName: row.agentName,
        completedTickets: 0,
        totalServiceTime: 0,
        activeDays: 0,
        serviceNames: new Set(),
        flags: new Set(),
      });
    }

    const agent = map.get(key);
    agent.completedTickets += row.completedTickets;
    agent.totalServiceTime += row.totalServiceTime;
    agent.activeDays = Math.max(agent.activeDays, toNumber(row.activeDays));
    agent.serviceNames.add(row.serviceName);
    row.flags.forEach((flag) => agent.flags.add(flag));
  });

  const totalTickets = Array.from(map.values()).reduce(
    (total, agent) => total + agent.completedTickets,
    0
  );
  const totalTime = Array.from(map.values()).reduce(
    (total, agent) => total + agent.totalServiceTime,
    0
  );
  const branchAverage = totalTickets ? totalTime / totalTickets : 0;

  const rows = Array.from(map.values()).map((agent) => {
    const averageServiceTime = agent.completedTickets
      ? agent.totalServiceTime / agent.completedTickets
      : 0;
    const ticketShare = totalTickets ? (agent.completedTickets / totalTickets) * 100 : 0;
    const serviceTimeShare = totalTime ? (agent.totalServiceTime / totalTime) * 100 : 0;
    const avgVsBranchAveragePct = branchAverage
      ? ((averageServiceTime - branchAverage) / branchAverage) * 100
      : 0;
    const flags = Array.from(agent.flags);
    const effectivenessBand =
      agent.completedTickets === 0
        ? "No completed tickets"
        : flags.length || avgVsBranchAveragePct >= 30
          ? "Needs review"
          : avgVsBranchAveragePct <= -15
            ? "Efficient"
            : "Expected range";

    return {
      agentId: agent.agentId,
      agentName: agent.agentName,
      completedTickets: agent.completedTickets,
      totalServiceTime: agent.totalServiceTime,
      averageServiceTime,
      ticketShare,
      serviceTimeShare,
      workloadIndex: serviceTimeShare - ticketShare,
      avgVsBranchAveragePct,
      activeDays: agent.activeDays,
      ticketsPerActiveDay: agent.activeDays ? agent.completedTickets / agent.activeDays : 0,
      serviceCount: uniqueCount(Array.from(agent.serviceNames)),
      flags,
      effectivenessBand,
    };
  });

  rankRows(rows, "completedTickets", "rankByTickets");
  rankRows(rows, "totalServiceTime", "rankByServiceTime");
  rankRows(rows, "averageServiceTime", "rankByAvgTime", "asc");
  return rows;
};

const buildServiceSummary = (branch = {}) => {
  if (Array.isArray(branch.serviceSummary) && branch.serviceSummary.length) {
    return branch.serviceSummary;
  }

  const rows = (branch.servicesData || []).map((service) => {
    const agents = service.agentsData || [];
    return {
      serviceId: service.serviceId,
      serviceName: service.serviceName,
      completedTickets: toNumber(service.completedTickets ?? service.ticketCount),
      totalServiceTime: toNumber(service.totalServiceTime),
      averageServiceTime: toNumber(service.averageServiceTime),
      activeAgentCount: agents.filter((agent) => resolveAgentServiceTicketCount(agent) > 0).length,
      noWorkAgentCount: agents.filter((agent) => resolveAgentServiceTicketCount(agent) === 0).length,
      reviewAgentCount: agents.filter((agent) => (agent.flags || []).length).length,
      unassignedCompletedTickets: toNumber(service.unassignedCompletedTickets),
    };
  });

  rankRows(rows, "completedTickets", "rankByTickets");
  rankRows(rows, "totalServiceTime", "rankByServiceTime");
  return rows;
};

export const getAgentServiceTimeInsights = (branch = {}) => {
  const agentSummary = buildAgentSummary(branch);
  const serviceSummary = buildServiceSummary(branch);
  const activeAgents = agentSummary.filter((agent) => agent.completedTickets > 0);
  const noWorkAgents = agentSummary.filter((agent) => agent.completedTickets === 0);
  const reviewAgents = agentSummary.filter((agent) => agent.effectivenessBand === "Needs review");
  const completedTickets =
    toNumber(branch.branchSummary?.completedTickets ?? branch.completedTickets ?? branch.ticketCount) ||
    serviceSummary.reduce((total, service) => total + toNumber(service.completedTickets), 0);
  const totalServiceTime =
    toNumber(branch.branchSummary?.totalServiceTime) ||
    serviceSummary.reduce((total, service) => total + toNumber(service.totalServiceTime), 0);

  return {
    branchSummary: {
      branchName: branch.branchName,
      serviceCount: toNumber(branch.branchSummary?.serviceCount ?? branch.numServices),
      completedTickets,
      totalServiceTime,
      averageServiceTime:
        toNumber(branch.branchSummary?.averageServiceTime) ||
        (completedTickets ? totalServiceTime / completedTickets : 0),
      activeAgentCount: toNumber(branch.branchSummary?.activeAgentCount) || activeAgents.length,
      noWorkAgentCount: toNumber(branch.branchSummary?.noWorkAgentCount) || noWorkAgents.length,
      reviewAgentCount: toNumber(branch.branchSummary?.reviewAgentCount) || reviewAgents.length,
      p90OutlierAgentCount: toNumber(branch.branchSummary?.p90OutlierAgentCount),
      unassignedCompletedTickets: toNumber(branch.branchSummary?.unassignedCompletedTickets),
      topAgentByTickets:
        branch.branchSummary?.topAgentByTickets ||
        [...activeAgents].sort((left, right) => right.completedTickets - left.completedTickets)[0],
      topAgentByServiceTime:
        branch.branchSummary?.topAgentByServiceTime ||
        [...activeAgents].sort((left, right) => right.totalServiceTime - left.totalServiceTime)[0],
      slowestAgent:
        branch.branchSummary?.slowestAgent ||
        [...activeAgents].sort((left, right) => right.averageServiceTime - left.averageServiceTime)[0],
    },
    agentSummary,
    serviceSummary,
    exceptionSummary: {
      noWorkAgents,
      reviewAgents,
      p90OutlierAgents: branch.exceptionSummary?.p90OutlierAgents || [],
    },
  };
};
